import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Switch, Select, MenuItem, CircularProgress,
  FormControl, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_BASE_URL } from '../apiConfig';

const USERS_API = API_BASE_URL.replace('/espacios', '/admin/users');

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'user', approved: true, can_view_dashboard: false });

  const [confirmDelete, setConfirmDelete] = useState({ open: false, uid: null, email: '' });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(USERS_API);
      setUsers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUpdate = async (uid, field, value) => {
    try {
      await axios.patch(`${USERS_API}/${uid}`, { [field]: value });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, [field]: value } : u));
    } catch (e) {
      console.error(e);
      alert('Error al actualizar usuario');
    }
  };

  const execDelete = async () => {
    const { uid } = confirmDelete;
    try {
      await axios.delete(`${USERS_API}/${uid}`);
      setUsers(prev => prev.filter(u => u.uid !== uid));
      setConfirmDelete({ open: false, uid: null, email: '' });
    } catch (e) {
      console.error(e);
      alert('Error al eliminar usuario');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email) return alert('El email es obligatorio');
    try {
      const { data } = await axios.post(USERS_API, newUser);
      setUsers(prev => [...prev, { ...newUser, uid: data.uid }]);
      setOpenAdd(false);
      setNewUser({ name: '', email: '', role: 'user', approved: true, can_view_dashboard: false });
    } catch (e) {
      console.error(e);
      alert('Error al crear usuario');
    }
  };

  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.03em' }}>
            Gestión de Usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Control de accesos, roles y permisos de la plataforma.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchUsers} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
            Agregar Usuario
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 4, 
            overflow: 'hidden', 
            border: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
          }}
        >
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: 'Nombre', width: 'auto' },
                    { label: 'Email', width: 'auto' },
                    { label: 'Rol', width: 150 },
                    { label: 'Dashboard', width: 120 },
                    { label: 'Aprobado', width: 180 },
                    { label: '', width: 60 }
                  ].map((col) => (
                    <TableCell 
                      key={col.label}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        py: 2,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        width: col.width
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.uid} hover>
                  <TableCell>{u.name || <Typography variant="caption" color="text.disabled">Sin nombre</Typography>}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={u.role || 'user'}
                        onChange={(e) => handleUpdate(u.uid, 'role', e.target.value)}
                        variant="outlined"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        <MenuItem value="user">Usuario</MenuItem>
                        <MenuItem value="admin">Administrador</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={u.can_view_dashboard === true}
                      onChange={(e) => handleUpdate(u.uid, 'can_view_dashboard', e.target.checked)}
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        size="small"
                        checked={u.approved === true}
                        onChange={(e) => handleUpdate(u.uid, 'approved', e.target.checked)}
                        color="primary"
                      />
                      <Chip 
                        label={u.approved ? "Aprobado" : "Pendiente"} 
                        color={u.approved ? "success" : "default"} 
                        size="small"
                        variant={u.approved ? "filled" : "outlined"}
                        sx={{ fontSize: '0.65rem', fontWeight: 800 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Eliminar Usuario">
                      <IconButton size="small" color="error" onClick={() => setConfirmDelete({ open: true, uid: u.uid, email: u.email })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {/* Add User Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle sx={{ fontWeight: 800 }}>Agregar Nuevo Usuario</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField 
              label="Email" 
              fullWidth 
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
            />
            <TextField 
              label="Nombre Full" 
              fullWidth 
              value={newUser.name}
              onChange={(e) => setNewUser({...newUser, name: e.target.value})}
            />
            <FormControl fullWidth>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
              >
                <MenuItem value="user">Usuario Estándar</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenAdd(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleAddUser} variant="contained" color="primary">Crear Usuario</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, uid: null, email: '' })}>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar permanentemente al usuario <strong>{confirmDelete.email}</strong>?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setConfirmDelete({ open: false, uid: null, email: '' })} color="inherit">Cancelar</Button>
          <Button onClick={execDelete} variant="contained" color="error">Eliminar Permanente</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
